/*
  DUK Geschenkverpackung – Warenkorb-Modul
  ------------------------------------------------------------------------------------------------
  Datenmodell (alles liegt im Shopify-Warenkorb, nichts im Browser):
  - Produktzeile:      Eigenschaft "Geschenk: 2" (EN "Gift: 2") = kommt in Geschenk 2
  - Verpackungszeile:  Produkt "Geschenkverpackung" (Handle geschenksverpackung), 1 Stück pro Geschenk, Eigenschaften
                       "Geschenk: 2", optional "Für: Anna", "Karte: …" (EN: Gift / For / Card).
                       Die Variante der Verpackungszeile ist das gewählte Papier.
  Der Preis entsteht dadurch automatisch: Summe der Verpackungszeilen.

  Ablauf beim Übernehmen: geänderte Zeilen per /cart/update.js auf 0, neue Zeilen per /cart/add.js
  (beides mit Section Rendering für den Drawer), danach das Theme-Event "cart:change".
  Auf der Warenkorbseite lädt die Seite neu – wie bei allen anderen Änderungen im Theme.

  Ein Abgleich (reconcile) hält Verpackungszeilen und Produkte synchron, wenn Kundinnen später
  Mengen ändern oder Produkte entfernen.
  „+“ an einem verpackten Produkt legt das neue Stück als eigene Zeile ohne Geschenk dazu (Matthias, 06.10.).

  Schalter (snippets/duk-gift-wrap.liquid, gw_messages): "messages": false blendet „Empfänger und Karte“
  aus, Für und Karte werden dann weder angeboten noch geschrieben (DUK 1.2, bis die Grußkarten kommen).
  "names": true bietet trotzdem den Namen an („Für“, ohne Kartentext): so weiß die Kundin, welches Geschenk für wen ist,
  das Team vermerkt den Namen am Geschenk (Matthias, 04.10.).
  Papiere = verfügbare Varianten des Verpackungsprodukts; bei nur einem Papier gibt es keine Auswahl.
  Hersteller des Papiers = Variantenfeld custom.paper_maker (z. B. Grafiche Tassotti): bei einem Papier in der
  Einleitung, bei mehreren an der Papierkachel.
  Wählt man eine Option, die weitere Angaben braucht (Produkte anhaken, aufteilen), scrollt das Panel ruhig dorthin (600–900 ms).

  DUK 1.3 (Entwurf 15/15b, Matthias 06.10.): Reiter „Einzeln · Zusammen · Aufteilen“ (1.2.4, vorher „Ein Geschenk · Einzeln · Aufteilen“) mit einem Satz darunter,
  „Ohne Verpackung“ als Link (nur wenn schon etwas verpackt ist). Ein Geschenk: Inhalt als Bilder, Name.
  Einzeln: Produkte mit Knopf, gewählte tragen ihre Geschenknummer am Bild. Aufteilen: Geschenke als Kacheln
  (Collage aus bis zu vier Bildern), „Was kommt wohin?“ mit kleinen Reitern „Ohne · 1 · 2“, „Für wen?“ mit einem
  Namensfeld je Geschenk. Papier (Bild, Satz) und Preis stehen im Kopf des Popovers (snippets/duk-gift-wrap.liquid).
  Aufteilen (06.10., 14:55): beginnt mit allem auf „Ohne“, „Für wen?“ steht vor „Wer bekommt was?“ (bis 15:16 „Was kommt
  wohin?“), die Reiter zeigen Nummer + Name; „Übernehmen“ erst, wenn etwas zugeordnet ist.
  Aufteilen (06.10., 16:10): „Für wen?“ mit „+ Weiteres Geschenk“ oben, „Wer bekommt was?“, die Kacheln unten.
  „So packen wir ein“ (06.10., 16:21): das Ergebnis steht bei allen drei Arten am Ende.
  Bilder laden sofort (nicht lazy): im Popover waren Inhalte sonst kurz leer.
*/

import * as Theme from 'theme';

const SYNC_EVENT = 'duk-gift:sync';
const POPOVER_ID = 'cart-drawer-gift-popover';

const KEYS = {
  de: { gift: 'Geschenk', to: 'Für', card: 'Karte' },
  en: { gift: 'Gift', to: 'For', card: 'Card' }
};
const GIFT_KEYS = ['Geschenk', 'Gift'];
const TO_KEYS = ['Für', 'For'];
const CARD_KEYS = ['Karte', 'Card'];

const CHECK_SVG = '<svg viewBox="0 0 16 16" width="12" height="12" focusable="false"><path d="M3 8.5l3.2 3L13 4.5" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>';
const GIFT_SVG = '<svg viewBox="0 0 16 16" width="12" height="12" focusable="false" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M2.5 7h11v6.5h-11zM1.5 4.5h13V7h-13zM8 4.5v9M8 4.5S6.9 1.6 5.2 2.1C3.7 2.6 4.6 4.5 8 4.5zM8 4.5s1.1-2.9 2.8-2.4c1.5.5.6 2.4-2.8 2.4z"/></svg>';
const PLUS_SVG = '<svg viewBox="0 0 20 20" width="12" height="12" focusable="false" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M10 3v14M3 10h14"/></svg>';
const CLOSE_SVG = '<svg viewBox="0 0 10 10" width="8" height="8" focusable="false" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.2"><path d="m1 1 8 8m0-8L1 9"/></svg>';
const MAX_TO = 40;
const MAX_CARD = 250;

const STRINGS = {
  de: {
    intro: 'Von Hand verpackt, auf Wunsch mit Karte. {price} pro Geschenk.',
    introFrom: 'Von Hand verpackt, auf Wunsch mit Karte. Ab {price} pro Geschenk.',
    introPlain: 'Von Hand verpackt. {price} pro Geschenk.',
    introPlainFrom: 'Von Hand verpackt. Ab {price} pro Geschenk.',
    introMaker: 'Geschenkpapier von {maker}. {price} pro Geschenk.',
    introMakerCard: 'Geschenkpapier von {maker}, auf Wunsch mit Karte. {price} pro Geschenk.',
    exampleAlt: 'Beispiel einer Geschenkverpackung von Duft und Kultur',
    legend: 'Verpackung wählen',
    none: 'Ohne Verpackung',
    together: 'Alles in ein Geschenk',
    togetherOne: 'Als Geschenk verpacken',
    togetherHint: 'Alles in ein Geschenk.',
    single: 'Einzelne Produkte verpacken',
    singleHint: 'Jedes Produkt als eigenes Geschenk.',
    items: 'Produkte',
    pickAll: 'Alle auswählen',
    pickClear: 'Auswahl aufheben',
    pickNone: 'Noch nichts ausgewählt',
    splitNone: 'Noch nichts zugeordnet',
    split: 'Selbst aufteilen',
    splitHint: 'Produkte auf mehrere Geschenke verteilen.',
    modeTogether: 'Zusammen',
    modeSingle: 'Einzeln',
    modeSplit: 'Aufteilen',
    oneGift: 'Ein Geschenk',
    forName: 'Für {name}',
    itemOne: '1 Produkt',
    itemMany: '{n} Produkte',
    whereTo: 'Wer bekommt was?',
    forWhom: 'Für wen?',
    addGift: 'Weiteres Geschenk',
    overview: 'So packen wir ein',
    addGiftLabel: 'Ein Geschenk dazunehmen',
    removeGift: 'Geschenk {n} entfernen',
    pickLabel: 'Einzeln verpacken: {title}',
    unpickLabel: 'Nicht verpacken: {title}',
    count: 'Anzahl Geschenke',
    less: 'Ein Geschenk weniger',
    more: 'Ein Geschenk mehr',
    chipNone: 'Ohne',
    chipNoneLabel: 'Nicht verpacken',
    chipGroup: 'Geschenk für {title}',
    gift: 'Geschenk {n}',
    empty: 'noch leer',
    details: 'Empfänger und Karte',
    optional: 'optional',
    contents: 'Inhalt',
    paper: 'Papier',
    paperGroup: 'Papier für {gift}',
    to: 'Für',
    toPlaceholder: 'Name (optional)',
    namePlaceholder: 'Name (optional)',
    namePlaceholderMany: 'Name (optional)',
    toHint: 'Wir vermerken den Namen am Geschenk.',
    toHintMany: 'Wir vermerken den Namen am Geschenk.',
    card: 'Kartentext',
    cardPlaceholder: 'Ein paar Worte für die Karte',
    giftOne: '1 Geschenk',
    giftMany: '{n} Geschenke',
    apply: 'Übernehmen',
    error: 'Das hat nicht geklappt. Bitte noch einmal versuchen.',
    changed: 'Der Warenkorb hat sich geändert. Bitte noch einmal prüfen.',
    noItems: 'Im Warenkorb ist nichts zum Verpacken.'
  },
  en: {
    intro: 'Wrapped by hand, with a card if you like. {price} per gift.',
    introFrom: 'Wrapped by hand, with a card if you like. From {price} per gift.',
    introPlain: 'Wrapped by hand. {price} per gift.',
    introPlainFrom: 'Wrapped by hand. From {price} per gift.',
    introMaker: 'Wrapping paper by {maker}. {price} per gift.',
    introMakerCard: 'Wrapping paper by {maker}, with a card if you like. {price} per gift.',
    exampleAlt: 'Example of gift wrapping by Duft und Kultur',
    legend: 'Choose gift wrapping',
    none: 'No gift wrapping',
    together: 'Everything in one gift',
    togetherOne: 'Wrap as a gift',
    togetherHint: 'Everything in one gift.',
    single: 'Wrap selected items',
    singleHint: 'Each item as its own gift.',
    items: 'Items',
    pickAll: 'Select all',
    pickClear: 'Clear selection',
    pickNone: 'Nothing selected yet',
    splitNone: 'Nothing assigned yet',
    split: 'Split into several gifts',
    splitHint: 'Spread items across several gifts.',
    modeTogether: 'Together',
    modeSingle: 'Separately',
    modeSplit: 'Split',
    oneGift: 'One gift',
    forName: 'For {name}',
    itemOne: '1 item',
    itemMany: '{n} items',
    whereTo: 'Who gets what?',
    forWhom: 'Who is it for?',
    addGift: 'Add another gift',
    overview: 'How we\'ll wrap it',
    addGiftLabel: 'Add a gift',
    removeGift: 'Remove gift {n}',
    pickLabel: 'Wrap separately: {title}',
    unpickLabel: 'Do not wrap: {title}',
    count: 'Number of gifts',
    less: 'One gift fewer',
    more: 'One gift more',
    chipNone: 'None',
    chipNoneLabel: 'Do not wrap',
    chipGroup: 'Gift for {title}',
    gift: 'Gift {n}',
    empty: 'empty',
    details: 'Recipient and card',
    optional: 'optional',
    contents: 'Contents',
    paper: 'Paper',
    paperGroup: 'Paper for {gift}',
    to: 'For',
    toPlaceholder: 'Name (optional)',
    namePlaceholder: 'Name (optional)',
    namePlaceholderMany: 'Name (optional)',
    toHint: 'We\'ll note the name on the gift.',
    toHintMany: 'We\'ll note the name on the gift.',
    card: 'Card message',
    cardPlaceholder: 'A few words for the card',
    giftOne: '1 gift',
    giftMany: '{n} gifts',
    apply: 'Apply',
    error: 'That didn’t work. Please try again.',
    changed: 'Your cart has changed. Please check again.',
    noItems: 'There is nothing to wrap in your cart.'
  }
};

/* ---------------------------------------------------------------------------------------------- */
/* Helpers                                                                                         */
/* ---------------------------------------------------------------------------------------------- */

function getConfig() {
  const element = document.getElementById('duk-gift-config');
  if (!element) return null;

  try {
    const config = JSON.parse(element.textContent);
    return config && config.variantId ? config : null;
  } catch (error) {
    return null;
  }
}

const langOf = (config) => (config && config.locale === 'de' ? 'de' : 'en');

// "messages": false in the config switches off recipient and card
const messagesOn = (config) => !config || config.messages !== false;

// "names": true offers the recipient's name alone, while the card stays off
const namesOn = (config) => messagesOn(config) || Boolean(config && config.names);

function translate(config, key, vars = {}) {
  let text = STRINGS[langOf(config)][key] ?? key;

  for (const [name, value] of Object.entries(vars)) {
    text = text.split(`{${name}}`).join(String(value));
  }

  return text;
}

const routesRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

function formatMoney(cents) {
  const format = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || '€{{amount_with_comma_separator}}';

  const format2 = (amount, decimals, thousands, decimal) => {
    const [integer, fraction] = (amount / 100).toFixed(decimals).split('.');
    const integerWithSeparators = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? integerWithSeparators + decimal + fraction : integerWithSeparators;
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
    switch (placeholder) {
      case 'amount_no_decimals': return format2(cents, 0, ',', '.');
      case 'amount_with_comma_separator': return format2(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return format2(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return format2(cents, 2, "'", '.');
      case 'amount_with_space_separator': return format2(cents, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator': return format2(cents, 0, ' ', ',');
      default: return format2(cents, 2, ',', '.');
    }
  });
}

function pickProperty(properties, keys) {
  if (!properties) return '';

  for (const key of keys) {
    if (properties[key] !== undefined && properties[key] !== null && properties[key] !== '') {
      return String(properties[key]);
    }
  }

  return '';
}

function packageOf(item) {
  const value = parseInt(pickProperty(item.properties, GIFT_KEYS), 10);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

const isWrap = (item, config) => Number(item.product_id) === Number(config.productId);
const isEligible = (item, config) => !isWrap(item, config) && item.requires_shipping && !item.gift_card;

// Papers = available variants of the wrap product. With only one, the picker stays hidden.
const papersOf = (config) => (Array.isArray(config.papers) ? config.papers : []);
const paperOf = (config, id) => papersOf(config).find((paper) => Number(paper.id) === Number(id)) || null;
const validPaper = (config, id) => (paperOf(config, id) ? Number(id) : Number(config.variantId));
const priceOf = (config, id) => {
  const paper = paperOf(config, id);
  return paper ? Number(paper.price) : Number(config.price);
};

function baseProperties(item) {
  const properties = {};

  for (const [key, value] of Object.entries(item.properties || {})) {
    if (GIFT_KEYS.includes(key) || value === null || value === '') continue;
    properties[key] = value;
  }

  return properties;
}

function cleanProperties(properties) {
  const clean = {};

  for (const [key, value] of Object.entries(properties || {})) {
    if (value === null || value === undefined || value === '') continue;
    clean[key] = String(value);
  }

  return clean;
}

/*
  Eine Warenkorb-Zeile erkennt man an Variante und Eigenschaften, nicht an ihrem Schlüssel (key). Mit einem Rabattcode im
  Warenkorb vergibt Shopify bei jeder Änderung einer Zeile allen Zeilen neue Schlüssel (live geprüft 08.10. mit welc5);
  ein Schlüssel von vorher liefert dann „no valid id or line parameter“. Darum planen wir mit der Identität und holen
  den Schlüssel erst im Moment des Schreibens aus dem Warenkorb, wie er gerade ist.
*/
function lineIdentity(item) {
  const properties = cleanProperties(item.properties);
  const sorted = Object.keys(properties).sort().map((key) => [key, properties[key]]);
  const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan ? item.selling_plan_allocation.selling_plan.id : '';
  return `${Number(item.variant_id)}|${plan || ''}|${JSON.stringify(sorted)}`;
}

function shortHash(text) {
  let hash = 0x811c9dc5;
  for (const char of String(text)) {
    hash ^= char.codePointAt(0);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36);
}

function keyFor(cart, identity) {
  const line = cart && Array.isArray(cart.items) ? cart.items.find((item) => lineIdentity(item) === identity) : null;
  return line ? line.key : null;
}

// Plan entries carry the identity; the key is looked up in the cart as it is right now
function keyedUpdates(cart, updates) {
  const keyed = {};

  for (const [identity, quantity] of Object.entries(updates)) {
    const key = keyFor(cart, identity);
    if (key) keyed[key] = quantity;
  }

  return keyed;
}

function sameProperties(a, b) {
  const left = cleanProperties(a);
  const right = cleanProperties(b);
  const leftKeys = Object.keys(left);

  return leftKeys.length === Object.keys(right).length && leftKeys.every((key) => left[key] === right[key]);
}

// Same gift, name and card, whichever language the keys were written in (Geschenk/Gift, Für/For, Karte/Card)
function sameMeaning(a, b) {
  const meaning = (properties) => {
    const clean = cleanProperties(properties);
    const out = {};
    for (const [key, value] of Object.entries(clean)) {
      const kind = GIFT_KEYS.includes(key) ? 'gift' : TO_KEYS.includes(key) ? 'to' : CARD_KEYS.includes(key) ? 'card' : key;
      out[kind] = value;
    }
    return out;
  };

  return sameProperties(meaning(a), meaning(b));
}

function lineToAddItem(item, properties) {
  const addItem = { id: item.variant_id, quantity: item.quantity, properties: cleanProperties(properties) };
  const sellingPlan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id;

  if (sellingPlan) addItem.selling_plan = sellingPlan;
  return addItem;
}

const cartSignature = (cart, config) => cart.items
  .filter((item) => isEligible(item, config))
  .map((item) => `${item.variant_id}x${item.quantity}`)
  .sort()
  .join('|');

// Same products, but lines rewritten elsewhere (automatic renumbering, a second tab). By identity, not by key: with a
// discount code every write gives every line a new key, without anything having changed for the panel
const cartLayout = (cart, config) => cart.items
  .filter((item) => isEligible(item, config))
  .map((item) => `${lineIdentity(item)}x${item.quantity}`)
  .sort()
  .join('|');

async function getCart() {
  const response = await fetch(`${routesRoot()}cart.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

async function getCachedCart() {
  try {
    if (Theme && Theme.fetchCart) return await Theme.fetchCart;
  } catch (error) { /* fall through */ }

  return getCart();
}

async function postJson(url, body) {
  const response = await fetch(`${routesRoot()}${url}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(json.description || json.message || response.statusText);
    error.status = response.status;
    throw error;
  }

  return json;
}

function bundledSections() {
  const sections = new Set();
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
  return [...sections];
}

/* ---------------------------------------------------------------------------------------------- */
/* Writing to the cart                                                                             */
/* ---------------------------------------------------------------------------------------------- */

/**
 * Puts the cart back exactly as it was (snapshot = cart.items before the change). A line key is variant + properties,
 * so re-adding a removed line recreates the same key. Used when a write fails halfway: no product may get lost.
 */
async function restoreSnapshot(snapshot) {
  const current = await getCart();
  // By identity (variant + properties): with a discount code the keys of the snapshot are long gone
  const before = new Map(snapshot.map((item) => [lineIdentity(item), item]));
  const now = new Map(current.items.map((item) => [lineIdentity(item), item]));
  const updates = {};
  const adds = [];

  for (const item of current.items) {
    const original = before.get(lineIdentity(item));

    if (!original) {
      updates[item.key] = 0;
    } else if (original.quantity !== item.quantity) {
      updates[item.key] = original.quantity;
    }
  }

  for (const item of snapshot) {
    if (!now.has(lineIdentity(item))) adds.push(lineToAddItem(item, item.properties));
  }

  if (Object.keys(updates).length > 0) await postJson('cart/update.js', { updates });

  // One line at a time (1.2.4, Fehlerliste Nr. 4): Shopify adds several items all or nothing, so one product that ran
  // short used to keep every other product out and left the cart empty. If a line no longer fits, as many as possible.
  let failed = null;

  for (const add of adds) {
    for (let quantity = add.quantity; quantity > 0; quantity -= 1) {
      try {
        await postJson('cart/add.js', { items: [{ ...add, quantity }] });
        break;
      } catch (error) {
        failed = error;
        if (error.status !== 422) break;
      }
    }
  }

  if (failed) throw failed;
}

/*
  Eine Warteschlange für alle Schreibzugriffe dieses Scripts (1.2.4, Fehlerliste Nr. 4): Übernehmen, automatische
  Korrektur und „+“ an verpackten Zeilen laufen nacheinander, jeder mit dem Warenkorb, wie er dann wirklich ist.
  Vorher konnte „Übernehmen“ während einer Korrektur ein Produkt verdoppeln.
*/
let writeTail = Promise.resolve();

function serialized(task) {
  const run = writeTail.then(task, task);
  writeTail = run.catch(() => {});
  return run;
}

/*
  Zeilen umschreiben statt entfernen und neu hinzufügen (1.2.4, Fehlerliste Nr. 4): cart/change.js setzt Menge und
  Eigenschaften einer bestehenden Zeile, ohne sie aus dem Warenkorb zu nehmen. Ist ein Produkt inzwischen knapp oder
  ausverkauft, bleibt es so im Warenkorb, statt beim Neu-Hinzufügen herauszufallen. Shopify kann Eigenschaften nicht
  auf leer setzen; solche Zeilen (Verpackung weg, keine anderen Eigenschaften) laufen weiter über Entfernen + Hinzufügen.
*/
// change = { identity, quantity, properties }; the key comes from the cart as it is now (see lineIdentity)
async function changeLine(cart, change) {
  const id = keyFor(cart, change.identity);

  if (!id) {
    const error = new Error('line not found');
    error.status = 400;
    throw error;
  }

  return postJson('cart/change.js', { id, quantity: change.quantity, properties: change.properties });
}

/*
  Plan: updates = { identity: quantity }, changes = [{ identity, quantity, properties }], adds = [add items],
  snapshot = cart.items at planning time. Every write answers with the cart as it is afterwards (update.js and
  change.js return the whole cart); the next write takes its key from there, never from the snapshot.
*/
async function executePlan({ updates, adds, snapshot, changes = [] }) {
  const hasUpdates = Object.keys(updates).length > 0;

  if (!hasUpdates && adds.length === 0 && changes.length === 0) return null;

  const sections = bundledSections();
  const sectionsParameter = sections.length > 0 ? { sections: sections.join(',') } : {};

  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

  try {
    // The snapshot was read inside the write queue just before planning, so its keys are still valid here
    let cart = { items: snapshot };

    // Order: remove first (so a renumbered line never meets the line it replaces), then rewrite in place, then add
    if (hasUpdates) {
      const keyed = keyedUpdates(cart, updates);

      if (Object.keys(keyed).length > 0) {
        cart = await postJson('cart/update.js', { updates: keyed, ...(adds.length > 0 || changes.length > 0 ? {} : sectionsParameter) });
      }
    }

    try {
      for (const change of changes) cart = await changeLine(cart, change);
    } catch (error) {
      if (snapshot) {
        try { await restoreSnapshot(snapshot); } catch (restoreError) { console.warn('[duk-gift] Wiederherstellen fehlgeschlagen', restoreError); }
      }

      throw error;
    }

    if (adds.length > 0) {
      let added;

      try {
        added = await postJson('cart/add.js', { items: adds, ...sectionsParameter });
      } catch (error) {
        if (snapshot) {
          try { await restoreSnapshot(snapshot); } catch (restoreError) { console.warn('[duk-gift] Wiederherstellen fehlgeschlagen', restoreError); }
        }

        throw error;
      }

      cart = await getCart();

      if (added.sections) {
        cart.sections = added.sections;
      }
    }

    // Nothing was written after all (the lines were gone already): continue with the real cart
    if (cart.items === snapshot) cart = { ...(await getCart()), sections: null };

    // Ended with changes: no sections came back, the theme fetches a fresh drawer (publishCart without sections)
    if (cart && changes.length > 0 && adds.length === 0) cart = { ...cart, sections: null };

    try {
      cart = await syncSummary(cart, getConfig());
    } catch (error) {
      console.warn('[duk-gift] Zusammenfassung nicht gespeichert', error);
    }

    return cart;
  } finally {
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
  }
}

/**
 * One readable line per gift for the team, stored as the order attribute "Geschenkverpackung"
 * (Shopify Admin → order → Additional details). Always German, whatever the storefront language.
 */
const SUMMARY_KEY = 'Geschenkverpackung';

function giftSummary(cart, config) {
  const wraps = cart.items.filter((item) => isWrap(item, config)).sort((a, b) => packageOf(a) - packageOf(b));

  return wraps.map((wrap) => {
    const number = packageOf(wrap);
    const contents = cart.items
      .filter((item) => isEligible(item, config) && packageOf(item) === number)
      .map((item) => {
        const variant = item.product_has_only_default_variant ? '' : ` ${item.variant_title || ''}`.trimEnd();
        return `${item.quantity}× ${item.vendor ? `${item.vendor} ` : ''}${item.product_title}${variant}`;
      });
    const to = pickProperty(wrap.properties, TO_KEYS);
    const card = pickProperty(wrap.properties, CARD_KEYS);
    const paper = wrap.product_has_only_default_variant ? '' : wrap.variant_title;
    // „Geschenk 1 (Sandra)“: the name goes on a note on the gift (Matthias, 04.10.)
    const details = [to, paper].filter(Boolean).join(', ');

    return `Geschenk ${number}${details ? ` (${details})` : ''}: ${contents.join(', ')}${card ? ` – Karte: „${card}“` : ''}`;
  }).join(' | ');
}

async function syncSummary(cart, config) {
  if (!cart || !config) return cart;

  const summary = giftSummary(cart, config);
  const current = (cart.attributes && cart.attributes[SUMMARY_KEY]) || '';

  if (summary === current) return cart;

  const updated = await postJson('cart/update.js', { attributes: { [SUMMARY_KEY]: summary } });
  if (cart.sections) updated.sections = cart.sections;
  return updated;
}

function publishCart(cart) {
  if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart') {
    window.location.reload();
    return;
  }

  if (cart && cart.sections && Object.keys(cart.sections).length > 0) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: SYNC_EVENT, cart } }));
  } else {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }
}

/**
 * final = { byUnit: Map(unitId → package number, 0 = not wrapped), messages: { [package]: { to, card } } }
 */
function buildPlan(cart, config, units, final) {
  const keys = KEYS[langOf(config)];
  const updates = {};
  const adds = [];
  const changes = [];

  // 1. Product lines
  const allocationByLine = new Map();

  for (const unit of units) {
    const allocation = allocationByLine.get(unit.identity) || new Map();
    const packageNumber = final.byUnit.get(unit.uid) || 0;
    allocation.set(packageNumber, (allocation.get(packageNumber) || 0) + 1);
    allocationByLine.set(unit.identity, allocation);
  }

  // Lines that keep their gift stay as they are, whatever language the gift key is in (1.2.4: on the English site
  // „Apply“ without a change used to rewrite every line written on the German site)
  const moves = [];

  for (const item of cart.items) {
    if (!isEligible(item, config)) continue;

    const allocation = allocationByLine.get(lineIdentity(item)) || new Map([[0, item.quantity]]);
    const currentPackage = packageOf(item);

    if (allocation.size === 1) {
      const [[packageNumber, count]] = [...allocation];

      if (count === item.quantity && packageNumber === currentPackage) continue;
    }

    const parts = [...allocation].filter(([, count]) => count > 0).map(([packageNumber, count]) => {
      const properties = baseProperties(item);
      if (packageNumber > 0) properties[keys.gift] = String(packageNumber);
      return { properties: cleanProperties(properties), count };
    });

    moves.push({ item, parts });
  }

  // One part of each line is rewritten in place (cart/change.js), the rest is added. In place only where it is safe:
  // properties not empty (Shopify cannot clear them), and no other line of the same product ends up with the same
  // properties (Shopify would have to merge them; that stays with cart/add.js, which merges as documented).
  const sameTarget = (variantId, properties, except) => cart.items.some((other) => other !== except && Number(other.variant_id) === Number(variantId) && sameProperties(other.properties, properties))
    || moves.some((move) => move.item !== except && Number(move.item.variant_id) === Number(variantId) && move.parts.some((part) => sameProperties(part.properties, properties)));

  for (const { item, parts } of moves) {
    const inPlace = parts.find((part) => Object.keys(part.properties).length > 0 && !sameTarget(item.variant_id, part.properties, item));

    if (inPlace) {
      changes.push({ identity: lineIdentity(item), quantity: inPlace.count, properties: inPlace.properties });
    } else {
      updates[lineIdentity(item)] = 0;
    }

    for (const part of parts) {
      if (part !== inPlace) adds.push(lineToAddItem({ ...item, quantity: part.count }, part.properties));
    }
  }

  // 2. Wrap lines: exactly one per package
  const packageNumbers = [...new Set([...final.byUnit.values()].filter((value) => value > 0))].sort((a, b) => a - b);
  const desired = packageNumbers.map((packageNumber) => {
    const message = final.messages[packageNumber] || {};
    const properties = { [keys.gift]: String(packageNumber) };

    if (namesOn(config) && message.to && message.to.trim()) properties[keys.to] = message.to.trim().slice(0, MAX_TO);
    if (messagesOn(config) && message.card && message.card.trim()) properties[keys.card] = message.card.trim().slice(0, MAX_CARD);

    return { id: validPaper(config, message.paper), properties, kept: false };
  });

  const wrapLines = cart.items.filter((item) => isWrap(item, config));
  const matched = new Set();

  for (const wish of desired) {
    const line = wrapLines.find((item) => !matched.has(item.key) && item.quantity === 1 && Number(item.variant_id) === wish.id && sameMeaning(item.properties, wish.properties));

    if (line) {
      matched.add(line.key);
      wish.kept = true;
    }
  }

  for (const line of wrapLines) {
    if (matched.has(line.key)) continue;

    updates[lineIdentity(line)] = 0;
  }

  for (const wish of desired) {
    if (!wish.kept) adds.push({ id: wish.id, quantity: 1, properties: wish.properties });
  }

  return { updates, adds, changes, snapshot: cart.items };
}

/* ---------------------------------------------------------------------------------------------- */
/* Keeping wrap lines in sync when the cart changes elsewhere                                      */
/* ---------------------------------------------------------------------------------------------- */

let reconciling = false;
let reconcileGuard = { state: '', attempts: 0 };

async function reconcile(cart) {
  const config = getConfig();

  if (!config || reconciling || !cart || !Array.isArray(cart.items)) return;

  const keys = KEYS[langOf(config)];
  const giftKeyOf = (item) => GIFT_KEYS.find((key) => item.properties && item.properties[key] !== undefined && item.properties[key] !== null) || keys.gift;
  const updates = {};
  const adds = [];
  const changes = [];

  // Gifts are always numbered 1, 2, 3 … without gaps: if one gift disappears, the others move up
  // (Matthias, 04.10.: a single remaining gift must not read „Geschenk 2“)
  const used = [...new Set(cart.items.filter((item) => isEligible(item, config)).map(packageOf).filter(Boolean))].sort((a, b) => a - b);
  const target = new Map(used.map((packageNumber, index) => [packageNumber, index + 1]));

  // 1. Product lines whose gift number moves up: rewrite the line in place with the new number (1.2.4: no longer
  //    removed and added again, so a product that ran low meanwhile cannot fall out). Lowest number first, so the
  //    number a line moves to is already free.
  const renumber = cart.items
    .filter((item) => isEligible(item, config) && packageOf(item) && target.get(packageOf(item)) !== packageOf(item))
    .sort((a, b) => packageOf(a) - packageOf(b));

  // The lines as they will be when each change runs (in this order): Geschenk 2 → 1 frees the 2 for Geschenk 3 → 2,
  // so the same product in several gifts moves up in place instead of being removed and added again (08.10.)
  const present = new Set(cart.items.map(lineIdentity));

  for (const item of renumber) {
    const properties = cleanProperties({ ...baseProperties(item), [giftKeyOf(item)]: String(target.get(packageOf(item))) });
    const moved = lineIdentity({ ...item, properties });
    const clash = present.has(moved);

    if (!clash) {
      present.delete(lineIdentity(item));
      present.add(moved);
    }

    if (clash) {
      updates[lineIdentity(item)] = 0;
      adds.push(lineToAddItem(item, properties));
    } else {
      changes.push({ identity: lineIdentity(item), quantity: item.quantity, properties });
    }
  }

  // 2. Wrap lines: exactly one per gift, quantity 1, same number as its products; names stay with their gift
  const seen = new Set();

  // Lowest number first (the cart lists new lines first), so the number a wrap moves to is already free
  for (const item of cart.items.filter((line) => isWrap(line, config)).sort((a, b) => packageOf(a) - packageOf(b))) {
    const packageNumber = packageOf(item);
    const next = target.get(packageNumber);

    if (!packageNumber || !next || seen.has(next)) {
      updates[lineIdentity(item)] = 0;
      continue;
    }

    seen.add(next);

    if (next !== packageNumber) {
      changes.push({ identity: lineIdentity(item), quantity: 1, properties: cleanProperties({ ...item.properties, [giftKeyOf(item)]: String(next) }) });
    } else if (item.quantity !== 1) {
      updates[lineIdentity(item)] = 1;
    }
  }

  // 3. Gifts without a wrap line
  for (const next of target.values()) {
    if (!seen.has(next)) adds.push({ id: config.variantId, quantity: 1, properties: { [keys.gift]: String(next) } });
  }

  if (Object.keys(updates).length === 0 && adds.length === 0 && changes.length === 0) {
    // Wrap lines are fine, but the contents of a gift may have changed: keep the team summary current
    try { await syncSummary(cart, config); } catch (error) { /* not critical */ }
    return;
  }

  // Never loop: the same cart is corrected at most three times. Before 1.2.4 every correction counted for the whole
  // session, so after three removals in a row an empty wrap stayed in the cart (Fehlerliste Nr. 1).
  const state = cart.items.map((item) => `${lineIdentity(item)}x${item.quantity}`).sort().join('|');
  const guard = reconcileGuard.state === state ? reconcileGuard : { state, attempts: 0 };
  if (guard.attempts >= 3) return;
  guard.attempts += 1;
  reconcileGuard = guard;

  reconciling = true;

  try {
    const result = await executePlan({ updates, adds, changes, snapshot: cart.items });

    // Let the theme fetch a fresh drawer: if someone kept tapping meanwhile, the plan's own render is already outdated
    if (result) publishCart({ ...result, sections: null });
  } catch (error) {
    console.warn('[duk-gift] Abgleich fehlgeschlagen', error);
    // The drawer may show lines (and keys) that no longer exist: let the theme draw it again
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  } finally {
    reconciling = false;
  }
}

// Quick changes (e.g. removing several products in a row, Matthias 04.10.): wait until the cart is quiet and work with
// the current cart instead of the one from the event, so the drawer never shows lines that no longer exist
let reconcileTimer = null;

document.addEventListener('cart:change', (event) => {
  if (!event.detail || event.detail.baseEvent === SYNC_EVENT || !event.detail.cart) return;

  clearTimeout(reconcileTimer);
  reconcileTimer = setTimeout(() => {
    serialized(() => getCart().then(reconcile)).catch(() => {});
  }, 600);
});

serialized(() => getCachedCart().then(reconcile)).catch(() => {});

/*
  „+“ an einem verpackten Produkt (Matthias, 06.10.): das neue Stück kommt als eigene Zeile ohne Geschenk dazu.
  Wer Bois Impérial für eine Freundin verpackt und dann noch eines für sich nimmt, schenkt ihr nicht still ein zweites.
  Verpacken geht danach wie immer im Konfigurator; kommt das Stück ins selbe Geschenk, legt Shopify beide wieder in
  eine Zeile (gleiche Variante, gleiche Eigenschaften). Weniger Stück und Entfernen laufen unverändert übers Theme.
  Verpackte Zeilen tragen data-duk-gift am <line-item> (snippets/duk-cart-line.liquid).
*/
function addLooseUnits(line, lineKey, quantity) {
  return serialized(() => addLooseUnitsNow(line, lineKey, quantity));
}

async function addLooseUnitsNow(line, lineKey, quantity) {
  const variantId = Number(String(lineKey || '').split(':')[0]);

  if (!variantId || !(quantity > 0)) return;

  const sections = bundledSections();
  const errorContainer = line.querySelector('.line-item__error-container');

  if (errorContainer) errorContainer.innerHTML = '';
  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

  try {
    const added = await postJson('cart/add.js', { items: [{ id: variantId, quantity }], ...(sections.length > 0 ? { sections: sections.join(',') } : {}) });
    const cart = await getCart();

    if (added.sections) cart.sections = added.sections;
    publishCart(cart);
    // Die neue Zeile kurz zeigen: Shopify setzt sie an den Anfang des Warenkorbs, nicht neben das Geschenk
    setTimeout(() => {
      const input = [...document.querySelectorAll('line-item:not([data-duk-gift]) [data-line-key]')].find((element) => element.getAttribute('data-line-key').split(':')[0] === String(variantId));
      const fresh = input && input.closest('line-item');

      if (!fresh) return;
      fresh.classList.add('duk-cart-line--new');
      fresh.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      setTimeout(() => fresh.classList.remove('duk-cart-line--new'), 1800);
    }, 120);
  } catch (error) {
    if (errorContainer) errorContainer.insertAdjacentHTML('afterbegin', `<p class="text-sm text-error" role="alert">${escapeHtml(error.message)}</p>`);
  } finally {
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
  }
}

/*
  Menge und Entfernen im Warenkorb-Drawer (1.2.5, Matthias 08.10.: „keine gültige ID“ beim zügigen Entfernen) laufen hier
  statt im Theme. Mit Rabattcode bekommen nach jeder Änderung alle Zeilen neue Schlüssel; ein Tipp, bevor der Drawer
  neu gezeichnet war, schickte den alten. Jetzt der Reihe nach (eine Änderung nach der anderen, gemeinsam mit dem
  Abgleich der Verpackung) und mit dem Schlüssel, den die Zeile gerade hat: erkannt an Variante und Eigenschaften
  (data-duk-variant / data-duk-props aus snippets/duk-cart-line.liquid). „+“ und „−“ zählen ab der aktuellen Menge,
  „Entfernen“ und eine eingetippte Zahl gelten wie angegeben. Gleiche Ereignisse wie das Theme (line-item:change,
  cart:change mit den Sections), der Drawer zeichnet sich also wie gewohnt neu.
*/
function lineFromElement(line) {
  const variant = Number(line.getAttribute('data-duk-variant'));
  if (!variant) return null;

  // Liquid gibt line_item.properties | json als Liste von Paaren aus ([["Gift","1"]]), leer als []
  let properties = {};
  try {
    const parsed = JSON.parse(line.getAttribute('data-duk-props') || '{}') || {};
    properties = Array.isArray(parsed) ? Object.fromEntries(parsed.filter((pair) => Array.isArray(pair) && pair.length === 2)) : parsed;
  } catch (error) {
    return null;
  }

  const plan = line.getAttribute('data-duk-plan');
  return { variant_id: variant, properties, selling_plan_allocation: plan ? { selling_plan: { id: Number(plan) } } : null };
}

function changeLineQuantity(line, change) {
  return serialized(() => changeLineQuantityNow(line, change)).catch(() => {});
}

async function changeLineQuantityNow(line, { delta = 0, absolute = null, key = '' }) {
  const item = lineFromElement(line);
  if (!item) return;

  const identity = lineIdentity(item);
  const errorContainer = line.querySelector('.line-item__error-container');
  const root = document.documentElement;

  if (errorContainer) errorContainer.innerHTML = '';
  root.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

  try {
    const current = await getCart();
    // By identity; if that fails for any reason, the key from the link (still right without a discount code)
    const existing = current.items.find((entry) => lineIdentity(entry) === identity) || current.items.find((entry) => key && entry.key === key);

    // The line is gone already (removed in another tab, or by a tap that was quicker): just draw the drawer again
    if (!existing) {
      root.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    const quantity = Math.max(0, absolute !== null ? absolute : existing.quantity + delta);
    if (quantity === existing.quantity) return;

    line.dispatchEvent(new CustomEvent('line-item:will-change', { bubbles: true, detail: { targetQuantity: quantity } }));

    const sections = bundledSections();
    const cart = await postJson('cart/change.js', { id: existing.key, quantity, ...(sections.length > 0 ? { sections: sections.join(',') } : {}) });

    if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart') {
      window.location.reload();
      return;
    }

    const after = cart.items.find((entry) => lineIdentity(entry) === identity);
    line.dispatchEvent(new CustomEvent('line-item:change', { bubbles: true, detail: { quantity: after ? after.quantity : 0, cart } }));
    root.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart } }));
  } catch (error) {
    if (errorContainer) errorContainer.insertAdjacentHTML('afterbegin', `<p class="text-sm text-error" role="alert">${escapeHtml(error.message)}</p>`);
    root.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  } finally {
    root.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
  }
}

// Capture phase on the document: runs before the theme's <line-item> handlers, which then never see the click
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('a[href*="cart/change"]') : null;
  const line = link && link.closest('line-item');

  if (!line || !(line.hasAttribute('data-duk-variant') || line.hasAttribute('data-duk-gift'))) return;

  const url = new URL(link.href, window.location.origin);
  const target = parseInt(url.searchParams.get('quantity'), 10);
  const input = line.querySelector('[data-line-key]');
  const current = input ? parseInt(input.defaultValue, 10) : NaN;

  if (!Number.isFinite(target)) return;

  // „+“ an einem verpackten Produkt: das neue Stück kommt als eigene Zeile ohne Geschenk dazu
  if (line.hasAttribute('data-duk-gift') && Number.isFinite(current) && target > current) {
    event.preventDefault();
    event.stopImmediatePropagation();
    addLooseUnits(line, line.getAttribute('data-duk-variant') || url.searchParams.get('id'), target - current);
    return;
  }

  // Zeilen aus einem älteren Drawer ohne Variante: wie bisher übers Theme
  if (!line.hasAttribute('data-duk-variant')) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const remove = link.classList.contains('duk-cart-line__remove') || !Number.isFinite(current);
  const key = url.searchParams.get('id') || '';
  changeLineQuantity(line, remove ? { absolute: target, key } : { delta: target - current, key });
}, true);

document.addEventListener('change', (event) => {
  const input = event.target instanceof Element ? event.target.closest('[data-line-key]') : null;
  const line = input && input.closest('line-item');

  if (!line || !(line.hasAttribute('data-duk-variant') || line.hasAttribute('data-duk-gift'))) return;

  const target = parseInt(input.value, 10);
  const current = parseInt(input.defaultValue, 10);

  if (line.hasAttribute('data-duk-gift') && target > current) {
    event.stopImmediatePropagation();
    input.value = input.defaultValue;
    addLooseUnits(line, line.getAttribute('data-duk-variant') || input.getAttribute('data-line-key'), target - current);
    return;
  }

  if (!line.hasAttribute('data-duk-variant') || !Number.isFinite(target)) return;

  event.stopImmediatePropagation();
  changeLineQuantity(line, { absolute: Math.max(0, target), key: input.getAttribute('data-line-key') || '' });
}, true);

// "Ändern" on a wrap line: in the drawer the theme opens the popover (aria-controls), on the cart page we scroll
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-duk-gift-edit]');

  if (!button || document.getElementById(button.getAttribute('aria-controls'))) return;

  const panel = document.getElementById('duk-gift');

  if (panel) {
    event.preventDefault();
    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    panel.querySelector('input, button')?.focus({ preventScroll: true });
  }
});

/* ---------------------------------------------------------------------------------------------- */
/* Panel                                                                                           */
/* ---------------------------------------------------------------------------------------------- */

// Die zuletzt übernommene Art (Ein Geschenk, Einzeln, Aufteilen) für diesen Besuch: „Ändern“ öffnet wieder dort
// (Matthias, 06.10.). Aus dem Warenkorb allein ist „Aufteilen“ mit je einem Produkt pro Geschenk nicht von „Einzeln“
// zu unterscheiden.
const MODE_KEY = 'duk:gift:mode';

function storedMode() {
  try {
    return sessionStorage.getItem(MODE_KEY) || '';
  } catch (error) {
    return '';
  }
}

function storeMode(mode) {
  try {
    if (mode && mode !== 'none') {
      sessionStorage.setItem(MODE_KEY, mode);
    } else {
      sessionStorage.removeItem(MODE_KEY);
    }
  } catch (error) {
    /* private mode */
  }
}

function stateFromCart(cart, config) {
  const units = [];

  for (const item of cart.items) {
    if (!isEligible(item, config)) continue;

    for (let index = 0; index < item.quantity; index++) {
      units.push({
        // Identity, not key: the key changes with every write when a discount code is in the cart
        uid: `${shortHash(lineIdentity(item))}#${index}`,
        key: item.key,
        identity: lineIdentity(item),
        variantId: Number(item.variant_id) || null,
        title: item.product_title,
        variant: item.product_has_only_default_variant ? '' : (item.variant_title || ''),
        vendor: item.vendor || '',
        image: item.image || '',
        packageNumber: packageOf(item)
      });
    }
  }

  const cartMessages = {};

  for (const item of cart.items) {
    if (!isWrap(item, config)) continue;

    const packageNumber = packageOf(item);

    if (packageNumber && !cartMessages[packageNumber]) {
      cartMessages[packageNumber] = {
        to: pickProperty(item.properties, TO_KEYS),
        card: pickProperty(item.properties, CARD_KEYS),
        paper: validPaper(config, item.variant_id)
      };
    }
  }

  const wrapped = units.filter((unit) => unit.packageNumber > 0);
  const distinct = new Set(wrapped.map((unit) => unit.packageNumber));
  let mode = 'none';
  let messages = { ...cartMessages };

  if (wrapped.length > 0) {
    if (wrapped.length === units.length && distinct.size === 1) {
      mode = 'together';
      messages = { 1: cartMessages[wrapped[0].packageNumber] || {} };
    } else if (distinct.size === wrapped.length && units.length > 1) {
      // Every wrapped item alone in its package (all of them or only some): „Einzelne Produkte verpacken“.
      // Messages are keyed by the item's place in the list, so they stay with the item when others are (un)picked.
      mode = 'single';
      messages = {};
      units.forEach((unit, index) => {
        if (unit.packageNumber > 0) messages[index + 1] = cartMessages[unit.packageNumber] || {};
      });
    } else {
      mode = 'split';
    }

    // Zuletzt „Aufteilen“ gewählt: so bleibt es, auch wenn jedes Geschenk nur ein Produkt hat (sähe sonst wie „Einzeln“
    // aus) oder alles in Geschenk 1 liegt
    if (storedMode() === 'split' && mode !== 'split' && units.length > 1) {
      mode = 'split';
      messages = { ...cartMessages };
    }
  }

  const assign = {};
  units.forEach((unit) => { assign[unit.uid] = unit.packageNumber; });

  const highest = Math.max(0, ...units.map((unit) => unit.packageNumber));

  return {
    mode,
    units,
    assign,
    // Items chosen under „Einzelne Produkte verpacken“: what is wrapped now stays chosen
    picked: new Set(wrapped.map((unit) => unit.uid)),
    messages,
    count: Math.min(Math.max(2, highest), Math.max(units.length, 1)),
    signature: cartSignature(cart, config),
    layout: cartLayout(cart, config)
  };
}

// Calm scroll for the gift panel. The browser's own smooth scroll felt too fast (Matthias, 04.10.):
// 600–900 ms depending on the distance, soft start and end. A touch or the mouse wheel stops it,
// with "reduce motion" it jumps straight there.
let glideStop = null;

const glide = (box, to, instant) => {
  if (glideStop) glideStop();

  const isWindow = box === window;
  const root = isWindow ? (document.scrollingElement || document.documentElement) : box;
  const max = root.scrollHeight - (isWindow ? window.innerHeight : box.clientHeight);
  let end = Math.max(0, to);
  if (max > 0) end = Math.min(end, max);

  const read = () => (isWindow ? window.scrollY : box.scrollTop);
  const write = (y) => {
    if (isWindow) window.scrollTo(0, y);
    else box.scrollTop = y;
  };

  const from = read();
  const distance = end - from;
  if (Math.abs(distance) < 1) return;

  // Theme CSS may set scroll-behavior: smooth, which would fight the frames below
  const style = root.style;
  const before = style.scrollBehavior;
  style.scrollBehavior = 'auto';

  if (instant) {
    write(end);
    style.scrollBehavior = before;
    return;
  }

  const duration = Math.min(900, 600 + Math.abs(distance) * 0.4);
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const now = () => (window.performance && typeof window.performance.now === 'function' ? window.performance.now() : Date.now());
  const frame = window.requestAnimationFrame ? window.requestAnimationFrame.bind(window) : (callback) => window.setTimeout(callback, 16);
  const events = isWindow ? window : box;
  const begin = now();
  let stopped = false;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    events.removeEventListener('wheel', stop);
    events.removeEventListener('touchstart', stop);
    style.scrollBehavior = before;
    if (glideStop === stop) glideStop = null;
  };

  events.addEventListener('wheel', stop, { passive: true });
  events.addEventListener('touchstart', stop, { passive: true });
  glideStop = stop;

  const step = () => {
    if (stopped) return;
    const t = Math.min(1, (now() - begin) / duration);
    write(from + distance * ease(t));
    if (t < 1) frame(step);
    else stop();
  };

  frame(step);
};

class DukGiftWrap extends HTMLElement {
  connectedCallback() {
    this.config = getConfig();

    if (!this.config) {
      this.hidden = true;
      return;
    }

    // The theme moves the cart drawer to <body> when it opens, which reconnects this element: set up only once
    if (!this.initialized) {
      this.initialized = true;
      this.uidPrefix = `duk-gift-${Math.random().toString(36).slice(2, 8)}`;
      this.openDetails = new Set();

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.addEventListener('toggle', this.onToggle, true);
    }

    this.drawerPopover = this.closest('x-drawer-popover');

    if (this.drawerPopover) {
      this.drawerPopover.addEventListener('dialog:before-show', this.onShow);
      this.drawerPopover.addEventListener('dialog:before-hide', this.onHide);
    } else {
      this.load();
    }
  }

  disconnectedCallback() {
    this.drawerPopover?.removeEventListener('dialog:before-show', this.onShow);
    this.drawerPopover?.removeEventListener('dialog:before-hide', this.onHide);
  }

  onShow = () => {
    this.load();
  };

  // Zurück zum Warenkorb übernimmt, was geändert wurde (Matthias, 06.10.: „nach Änderung und Zurück keine Änderung im
  // Warenkorb“). Nur Ansehen und Zurück ändert nichts. 1.2.4 (Matthias, 07.10., 22:42): War noch nichts verpackt, bricht
  // der Pfeil ab, auch nach Reiterwechsel oder Namen; Verpackung kommt nur mit „Übernehmen“ in den Warenkorb.
  onHide = () => {
    if (!this.dirty || this.applying || this.blocked()) return;
    if (!this.state || this.state.initialMode === 'none') return;

    const button = this.querySelector('[data-action="apply"]');
    if (button) this.apply(button);
  };

  t(key, vars) {
    return translate(this.config, key, vars);
  }

  async load(cart) {
    try {
      cart = cart || await getCachedCart();
    } catch (error) {
      cart = await getCart();
    }

    this.state = stateFromCart(cart, this.config);
    this.messageStore = {};
    this.dirty = false;
    // Noch nichts verpackt: bei mehreren Produkten öffnet der Konfigurator mit „Einzeln“, alle ausgewählt (Matthias,
    // 07.10.); bei einem Produkt gibt es keine Reiter, dort heißt es „Als Geschenk verpacken“
    this.state.initialMode = this.state.mode;
    if (this.state.mode === 'none') {
      this.state.mode = this.state.units.length > 1 ? 'single' : 'together';
      if (this.state.mode === 'single') this.state.picked = new Set(this.state.units.map((unit) => unit.uid));
    }
    this.openDetails = new Set(Object.entries(this.state.messages).filter(([, message]) => message && (message.to || message.card)).map(([number]) => Number(number)));
    this.render();
  }

  /* -- Derived data ---------------------------------------------------------------------------- */

  packages() {
    const { mode, units, count, assign } = this.state;

    if (mode === 'none') return [];
    if (mode === 'together') return [{ number: 1, units }];

    // Chosen items only; "number" keys paper and card (place in the list), "display" is the gift number shown
    if (mode === 'single') {
      let display = 0;
      return units
        .map((unit, index) => ({ number: index + 1, units: [unit] }))
        .filter((entry) => this.state.picked.has(entry.units[0].uid))
        .map((entry) => ({ ...entry, display: ++display }));
    }

    const list = [];

    for (let number = 1; number <= count; number++) {
      list.push({ number, units: units.filter((unit) => assign[unit.uid] === number) });
    }

    return list;
  }

  finalAssignment() {
    const { mode, units, assign, messages } = this.state;
    const byUnit = new Map();
    const finalMessages = {};

    if (mode === 'together') {
      units.forEach((unit) => byUnit.set(unit.uid, 1));
      finalMessages[1] = messages[1] || {};
    } else if (mode === 'single') {
      let next = 0;

      units.forEach((unit, index) => {
        if (!this.state.picked.has(unit.uid)) {
          byUnit.set(unit.uid, 0);
          return;
        }

        next += 1;
        byUnit.set(unit.uid, next);
        finalMessages[next] = messages[index + 1] || {};
      });
    } else if (mode === 'split') {
      const used = [...new Set(units.map((unit) => assign[unit.uid]).filter((value) => value > 0))].sort((a, b) => a - b);
      const renumber = new Map(used.map((old, index) => [old, index + 1]));

      units.forEach((unit) => byUnit.set(unit.uid, renumber.get(assign[unit.uid]) || 0));
      used.forEach((old) => { finalMessages[renumber.get(old)] = messages[old] || {}; });
    } else {
      units.forEach((unit) => byUnit.set(unit.uid, 0));
    }

    return { byUnit, messages: finalMessages };
  }

  packageCount() {
    return this.packages().filter((entry) => entry.units.length > 0).length;
  }

  /* -- Rendering ------------------------------------------------------------------------------- */

  unitLabel(unit) {
    return [unit.title, unit.variant].filter(Boolean).join(' · ');
  }

  // Sobald ein Name eingetragen ist, heißt das Geschenk nach ihm: „Für Hanna“ (Matthias, 06.10.). Ohne Namen bleibt
  // „Geschenk 1“ bzw. „Ein Geschenk“. Beim Aufteilen steht die Nummer als Abzeichen auf der Kachel, wie bei den Produkten.
  recipient(number) {
    const message = this.state.messages[number] || {};
    return namesOn(this.config) && typeof message.to === 'string' ? message.to.trim().slice(0, MAX_TO) : '';
  }

  paintRecipient(number) {
    const name = this.recipient(number);

    this.querySelectorAll(`[data-gift-title="${number}"]`).forEach((element) => {
      element.textContent = name ? this.t('forName', { name }) : element.getAttribute('data-default');
    });

    // Reiter bei „Was kommt wohin?“: Nummer im Kreis, daneben der Name, sobald einer eingetragen ist
    this.querySelectorAll(`[data-gift-chip="${number}"]`).forEach((element) => {
      element.innerHTML = this.chipInner(number);
    });
  }

  // Nummer + Name (Matthias, 06.10., Variante C): die Kreise trennen die Namen und passen zu den Ziffern an den Bildern
  chipInner(number) {
    const name = this.recipient(number);
    return `<span class="duk-gift__chip-n" aria-hidden="true">${number}</span>${name ? `<span class="duk-gift__chip-t">${escapeHtml(name)}</span>` : ''}`;
  }

  imageUrl(src, width) {
    return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  }

  // Produktbild auf Off-White (multiply färbt den weißen Hintergrund ein), optional mit Geschenknummer
  renderThumb(unit, badge) {
    return `
      <span class="duk-gift-ps duk-gift__thumb">
        ${unit.image ? `<img src="${escapeHtml(this.imageUrl(unit.image, 120))}" alt="" width="52" height="61">` : ''}
        ${badge ? `<span class="duk-gift__badge" aria-hidden="true">${escapeHtml(badge)}</span>` : ''}
      </span>
    `;
  }

  // Inhalt eines Geschenks: ein Bild oder ein Raster aus bis zu vier Bildern, ab fünf Produkten „+n“ auf dem vierten.
  // Ein Produkt mehrmals: ein Bild mit „2×“ links unten, sonst stünden bei „3 Produkte“ nur zwei Bilder da (Knis, 06.10.)
  // Kacheln (grid): ein Produkt groß, ab zwei Produkten immer vier Felder wie die Kacheln der Wunschliste, leere Felder
  // bleiben Off-White (Matthias, 06.10., 16:46)
  // Gleiches Produkt aus mehreren Warenkorb-Zeilen (z. B. vorher einzeln verpackt) zählt als ein Bild mit „3×“ (1.2.4)
  renderCollage(units, { grid = false } = {}) {
    const same = (unit) => unit.variantId || unit.identity;
    const seen = new Set();
    const counts = new Map();
    units.forEach((unit) => counts.set(same(unit), (counts.get(same(unit)) || 0) + 1));
    const distinct = units.filter((unit) => (seen.has(same(unit)) ? false : seen.add(same(unit))));

    if (distinct.length === 0) return '<span class="duk-gift__collage duk-gift__collage--empty" aria-hidden="true"></span>';

    const shown = distinct.slice(0, 4);
    const more = distinct.length - shown.length;
    const pad = grid && shown.length > 1 ? '<span class="duk-gift-ps"></span>'.repeat(4 - shown.length) : '';

    return `
      <span class="duk-gift__collage ${shown.length === 1 ? 'duk-gift__collage--one' : ''}" aria-hidden="true">
        ${shown.map((unit, index) => `
          <span class="duk-gift-ps">
            ${unit.image ? `<img src="${escapeHtml(this.imageUrl(unit.image, 200))}" alt="" width="100" height="117">` : ''}
            ${counts.get(same(unit)) > 1 ? `<span class="duk-gift__qty">${counts.get(same(unit))}×</span>` : ''}
            ${index === 3 && more > 0 ? `<span class="duk-gift__more">+${more}</span>` : ''}
          </span>
        `).join('')}${pad}
      </span>
    `;
  }

  renderUnitText(unit) {
    return `
      <span class="duk-gift__unit-text">
        ${unit.vendor ? `<span class="duk-gift__cap">${escapeHtml(unit.vendor)}</span>` : ''}
        <span class="duk-gift__unit-name">${escapeHtml(this.unitLabel(unit))}</span>
      </span>
    `;
  }

  // Reiter „Ein Geschenk · Einzeln · Aufteilen“, darunter ein Satz zur gewählten Art. Bei einem Produkt keine Wahl.
  renderModes() {
    const { units, mode } = this.state;

    if (units.length === 1) return '';

    // „Einzeln“ zuerst (Matthias, 07.10.: bei mehreren Produkten öffnet das Fenster damit)
    const options = [['single', 'modeSingle'], ['together', 'modeTogether'], ['split', 'modeSplit']];
    const hint = { together: 'togetherHint', single: 'singleHint', split: 'splitHint' }[mode];

    return `
      <fieldset class="duk-gift__modes">
        <legend class="sr-only">${escapeHtml(this.t('legend'))}</legend>
        <div class="duk-gift__segments">
          ${options.map(([value, label]) => `
            <label class="duk-gift__segment">
              <input class="sr-only" type="radio" name="${this.uidPrefix}-mode" value="${value}" data-focus-key="mode-${value}" ${mode === value ? 'checked' : ''}>
              <span>${escapeHtml(this.t(label))}</span>
            </label>
          `).join('')}
        </div>
        ${hint ? `<p class="duk-gift__hint">${escapeHtml(this.t(hint))}</p>` : ''}
      </fieldset>
    `;
  }

  // Ein Geschenk: Titel, Anzahl · Preis, darunter der Inhalt als Bilder in einer Reihe. Das Papier steht seit DUK 1.3 im
  // Kopf, für alle drei Arten (Matthias, 06.10., Entwurf 15c)
  renderTogether() {
    const { units } = this.state;
    const paperId = (this.state.messages[1] || {}).paper;
    const count = units.length;

    return `
      <div class="duk-gift__one">
        <div class="duk-gift__one-text">
          <p class="duk-gift__title" data-gift-title="1" data-default="${escapeHtml(this.t('oneGift'))}">${escapeHtml(this.recipient(1) ? this.t('forName', { name: this.recipient(1) }) : this.t('oneGift'))}</p>
          <p class="duk-gift__muted">${escapeHtml(this.t(count === 1 ? 'itemOne' : 'itemMany', { n: count }))} · ${formatMoney(priceOf(this.config, paperId))}</p>
        </div>
        ${this.renderCollage(units)}
      </div>
    `;
  }

  // Aufteilen: oben „Für wen?“ (names, mit „Weiteres Geschenk“), dann „Wer bekommt was?“ mit kleinen Reitern je Produkt,
  // ganz unten die Kacheln als Übersicht „So packen wir ein“.
  // Erst wer, dann was (Matthias, 06.10.): die Reiter tragen beim Zuordnen schon die Namen.
  // Kacheln nur für befüllte Geschenke und erst ab dem ersten zugeordneten Produkt, unten, damit beim ersten Antippen
  // nichts springt (Matthias, 06.10., 15:56: „3 grosse leere Felder … verwirrend“).
  renderSplit(names = '') {
    const { units, count, assign } = this.state;
    const numbers = Array.from({ length: count }, (value, index) => index + 1);

    // Ohne Namensblock (Namen aus oder je Geschenk ein eigener Abschnitt): „Weiteres Geschenk“ rechts neben der Überschrift
    const addLink = !names && count < units.length
      ? `<button type="button" class="duk-gift__link" data-action="increase" data-focus-key="increase">${escapeHtml(this.t('addGift'))}</button>`
      : '';

    return `
      <div class="duk-gift__split">
        ${names}
        <div class="duk-gift__group">
          <div class="duk-gift__group-head">
            <p class="duk-gift__label">${escapeHtml(this.t('whereTo'))}</p>
            ${addLink}
          </div>
          <ul class="duk-gift__rows unstyled-list" role="list">
            ${units.map((unit) => `
              <li class="duk-gift__row">
                ${this.renderThumb(unit, assign[unit.uid] > 0 ? String(assign[unit.uid]) : '')}
                <div class="duk-gift__row-main">
                  ${this.renderUnitText(unit)}
                  <div class="duk-gift__mini" role="group" aria-label="${escapeHtml(this.t('chipGroup', { title: this.unitLabel(unit) }))}">
                    <button type="button" data-action="assign" data-uid="${escapeHtml(unit.uid)}" data-package="0" data-focus-key="assign-${escapeHtml(unit.uid)}-0" aria-pressed="${assign[unit.uid] === 0}" aria-label="${escapeHtml(this.t('chipNoneLabel'))}">${escapeHtml(this.t('chipNone'))}</button>
                    ${numbers.map((number) => `
                      <button type="button" data-action="assign" data-uid="${escapeHtml(unit.uid)}" data-package="${number}" data-focus-key="assign-${escapeHtml(unit.uid)}-${number}" aria-pressed="${assign[unit.uid] === number}" aria-label="${escapeHtml(this.t('gift', { n: number }))}" data-gift-chip="${number}">${this.chipInner(number)}</button>
                    `).join('')}
                  </div>
                </div>
              </li>
            `).join('')}
          </ul>
        </div>
        ${this.renderOverview()}
      </div>
    `;
  }

  // „So packen wir ein“ am Ende, bei allen drei Arten (Matthias, 06.10., 16:21): erst wählen und benennen, unten das
  // Ergebnis. Nur befüllte Geschenke, erst wenn es eines gibt.
  // - Ein Geschenk: der Inhalt als Bilder in einer Reihe über die ganze Breite, darunter Name, Anzahl und Preis.
  // - Einzeln und Aufteilen: Kacheln (Collage, Nummer, „Für Hanna“ bzw. „Geschenk 1“); bei Einzeln darunter das Produkt,
  //   beim Aufteilen die Anzahl.
  renderOverview() {
    const { mode } = this.state;
    const packages = this.packages().filter((entry) => entry.units.length > 0);

    if (packages.length === 0) return '';

    const label = `<p class="duk-gift__label">${escapeHtml(this.t('overview'))}</p>`;

    if (mode === 'together') {
      const { units } = packages[0];
      const paperId = (this.state.messages[1] || {}).paper;
      const name = this.recipient(1);

      // 1.2.4 (Matthias, 07.10., 22:53): dieselbe Kachel wie bei Einzeln und Aufteilen, eine Kachel = ein Geschenk.
      // Vorher eine Bilderreihe, die wie mehrere Geschenke aussah.
      return `
        <div class="duk-gift__group duk-gift__overview">
          ${label}
          <div class="duk-gift__tiles">
            <div class="duk-gift__tile">
              <span class="duk-gift__tile-media">${this.renderCollage(units, { grid: true })}</span>
              <span class="duk-gift__tile-text">
                <span class="duk-gift__title duk-gift__title--sm" data-gift-title="1" data-default="${escapeHtml(this.t('oneGift'))}">${escapeHtml(name ? this.t('forName', { name }) : this.t('oneGift'))}</span>
                <span class="duk-gift__muted duk-gift__small duk-gift__tile-sub">${escapeHtml(this.t(units.length === 1 ? 'itemOne' : 'itemMany', { n: units.length }))} · ${formatMoney(priceOf(this.config, paperId))}</span>
              </span>
            </div>
          </div>
        </div>
      `;
    }

    const tiles = packages.map(({ number, display, units }) => {
      const shown = display || number;
      const gift = this.t('gift', { n: shown });
      const name = this.recipient(number);
      const sub = mode === 'single' ? this.unitLabel(units[0]) : this.t(units.length === 1 ? 'itemOne' : 'itemMany', { n: units.length });

      return `
        <div class="duk-gift__tile">
          <span class="duk-gift__tile-media">
            ${this.renderCollage(units, { grid: true })}
            <span class="duk-gift__tile-icon duk-gift__tile-icon--num" aria-hidden="true">${shown}</span>
          </span>
          <span class="duk-gift__tile-text">
            <span class="duk-gift__title duk-gift__title--sm" data-gift-title="${number}" data-default="${escapeHtml(gift)}">${escapeHtml(name ? this.t('forName', { name }) : gift)}</span>
            <span class="duk-gift__muted duk-gift__small duk-gift__tile-sub">${escapeHtml(sub)}</span>
          </span>
        </div>
      `;
    });

    return `
      <div class="duk-gift__group duk-gift__overview">
        ${label}
        <div class="duk-gift__tiles ${tiles.length >= 4 ? 'duk-gift__tiles--4' : ''}">${tiles.join('')}</div>
      </div>
    `;
  }

  // Einzeln: jedes gewählte Produkt in ein eigenes Paket. Knopf rechts wie im Warenkorb, die Nummer am Bild
  renderPick() {
    const { units, picked } = this.state;
    const all = units.every((unit) => picked.has(unit.uid));
    let number = 0;

    return `
      <div class="duk-gift__pick duk-gift__group">
        <div class="duk-gift__group-head">
          <p class="duk-gift__label">${escapeHtml(this.t('items'))}</p>
          <button type="button" class="duk-gift__link" data-action="pick-all" data-focus-key="pick-all">${escapeHtml(this.t(all ? 'pickClear' : 'pickAll'))}</button>
        </div>
        <ul class="duk-gift__rows unstyled-list" role="list">
          ${units.map((unit) => {
            const on = picked.has(unit.uid);
            if (on) number += 1;

            return `
              <li class="duk-gift__row">
                ${this.renderThumb(unit, on ? String(number) : '')}
                <div class="duk-gift__row-main">${this.renderUnitText(unit)}</div>
                <button type="button" class="duk-gift__toggle" data-action="pick" data-uid="${escapeHtml(unit.uid)}" data-focus-key="pick-${escapeHtml(unit.uid)}" aria-pressed="${on}" aria-label="${escapeHtml(this.t(on ? 'unpickLabel' : 'pickLabel', { title: this.unitLabel(unit) }))}">${on ? CHECK_SVG : PLUS_SVG}</button>
              </li>
            `;
          }).join('')}
        </ul>
      </div>
    `;
  }

  // Namen: bei einem Geschenk ein Feld, sonst „Für wen?“ mit einem Feld je Geschenk (Matthias, 04.10./06.10.).
  // Vor jedem Feld die Nummer im Kreis wie an Kacheln, Bildern und Reitern (Matthias, 06.10., 14:55: „dann ists einheitlich“).
  // Unter der Überschrift ein Satz, wozu der Name gut ist; im Feld nur „Name“ (Matthias, 06.10., 15:15).
  renderNames() {
    if (!namesOn(this.config)) return '';

    // Aufteilen: auch leere Geschenke bekommen ein Feld – erst benennen, dann zuordnen (Matthias, 06.10.: „3. Geschenk,
    // keine Option für einen 3. Namen“)
    const packages = this.state.mode === 'split' ? this.packages() : this.packages().filter((entry) => entry.units.length > 0);

    if (packages.length === 0) return '';
    if (this.state.mode === 'together') return this.renderName(1, this.state.messages[1] || {});

    const hintId = `${this.uidPrefix}-names-hint`;

    // Aufteilen: letzte Zeile „Weiteres Geschenk“ mit der nächsten Nummer in Grau (statt der Plus-Kachel), ab drei
    // Geschenken ein x je Zeile; zwei bleiben immer. Ein befülltes Geschenk entfernen legt seine Produkte auf „Ohne“.
    const split = this.state.mode === 'split';
    const { count, units } = this.state;
    const add = split && count < units.length;
    const removable = split && count > 2;

    return `
      <div class="duk-gift__group duk-gift__names">
        <div class="duk-gift__group-intro">
          <p class="duk-gift__label">${escapeHtml(this.t('forWhom'))}</p>
          <p id="${hintId}" class="duk-gift__muted duk-gift__small">${escapeHtml(this.t('toHintMany'))}</p>
        </div>
        ${packages.map(({ number, display }) => {
          const id = `${this.uidPrefix}-to-${number}`;
          const message = this.state.messages[number] || {};

          return `
            <div class="duk-gift__name-row ${removable ? 'duk-gift__name-row--x' : ''}">
              <label for="${id}" class="duk-gift__name-n"><span aria-hidden="true">${display || number}</span><span class="sr-only">${escapeHtml(this.t('gift', { n: display || number }))}</span></label>
              <input id="${id}" class="input duk-gift__input" type="text" maxlength="${MAX_TO}" autocomplete="off" enterkeyhint="done" placeholder="${escapeHtml(this.t('namePlaceholderMany'))}" aria-describedby="${hintId}" data-field="to" data-package="${number}" data-focus-key="to-${number}" value="${escapeHtml(message.to || '')}">
              ${removable ? `<button type="button" class="duk-gift__remove" data-action="remove-gift" data-package="${number}" data-focus-key="remove-${number}" aria-label="${escapeHtml(this.t('removeGift', { n: number }))}">${CLOSE_SVG}</button>` : ''}
            </div>
          `;
        }).join('')}
        ${add ? `
          <button type="button" class="duk-gift__name-row duk-gift__name-add ${removable ? 'duk-gift__name-row--x' : ''}" data-action="increase" data-focus-key="increase" aria-label="${escapeHtml(this.t('addGiftLabel'))}">
            <span class="duk-gift__name-n duk-gift__name-n--next" aria-hidden="true">${count + 1}</span>
            <span class="duk-gift__name-add-field" aria-hidden="true">${PLUS_SVG}<span>${escapeHtml(this.t('addGift'))}</span></span>
          </button>
        ` : ''}
      </div>
    `;
  }

  renderPackages() {
    const packages = this.packages();

    if (packages.length === 0) return '';

    const alwaysOpen = this.state.mode === 'together';

    return `
      <div class="duk-gift__packages">
        ${packages.map(({ number, units, display }) => {
          const message = this.state.messages[number] || {};
          const open = alwaysOpen || this.openDetails.has(number);

          return `
            <div class="duk-gift__package ${units.length === 0 ? 'duk-gift__package--empty' : ''}">
              <p class="duk-gift__package-title">${escapeHtml(this.t('gift', { n: display || number }))}</p>

              ${units.length > 0 ? `
                <div class="duk-gift__section">
                  <p class="duk-gift__section-label">${escapeHtml(this.t('contents'))}</p>
                  ${this.renderContents(units)}
                </div>
              ` : `<p class="duk-gift__package-items">${escapeHtml(this.t('empty'))}</p>`}

              ${units.length > 0 ? this.renderPapers(number) : ''}

              ${units.length > 0 && !messagesOn(this.config) && namesOn(this.config) ? this.renderName(number, message) : ''}

              ${units.length > 0 && messagesOn(this.config) ? `
                <details class="duk-gift__details" data-package="${number}" ${open ? 'open' : ''}>
                  <summary data-focus-key="details-${number}">
                    <span>${escapeHtml(this.t('details'))}</span>
                    <span class="duk-gift__optional">${escapeHtml(this.t('optional'))}</span>
                  </summary>

                  <div class="duk-gift__fields">
                    <div class="form-control">
                      <label for="${this.uidPrefix}-to-${number}" class="text-subdued justify-self-start">${escapeHtml(this.t('to'))}</label>
                      <input id="${this.uidPrefix}-to-${number}" class="input" type="text" maxlength="${MAX_TO}" autocomplete="off" enterkeyhint="done" placeholder="${escapeHtml(this.t('toPlaceholder'))}" data-field="to" data-package="${number}" data-focus-key="to-${number}" value="${escapeHtml(message.to || '')}">
                    </div>

                    <div class="form-control">
                      <label for="${this.uidPrefix}-card-${number}" class="text-subdued justify-self-start">${escapeHtml(this.t('card'))}</label>
                      <textarea id="${this.uidPrefix}-card-${number}" class="textarea" rows="3" maxlength="${MAX_CARD}" placeholder="${escapeHtml(this.t('cardPlaceholder'))}" data-field="card" data-package="${number}" data-focus-key="card-${number}" style="--textarea-min-rows: 3">${escapeHtml(message.card || '')}</textarea>
                    </div>
                  </div>
                </details>
              ` : ''}
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  // Name only (no card): one field under the contents, with a line on what happens with it.
  // One line: "optional" sits in the placeholder, "Für"/"For" stays as label for screen readers (Matthias, 04.10.)
  renderName(number, message) {
    const id = `${this.uidPrefix}-to-${number}`;

    return `
      <div class="duk-gift__section duk-gift__name">
        <label for="${id}" class="sr-only">${escapeHtml(this.t('to'))}</label>
        <input id="${id}" class="input duk-gift__input" type="text" maxlength="${MAX_TO}" autocomplete="off" enterkeyhint="done" placeholder="${escapeHtml(this.t('namePlaceholder'))}" aria-describedby="${id}-hint" data-field="to" data-package="${number}" data-focus-key="to-${number}" value="${escapeHtml(message.to || '')}">
        <p id="${id}-hint" class="duk-gift__muted duk-gift__small">${escapeHtml(this.t('toHint'))}</p>
      </div>
    `;
  }

  /* What goes into one package, as product images (one per cart line, with "3×" when several) */
  renderContents(units) {
    const groups = [];
    const byKey = new Map();

    units.forEach((unit) => {
      if (byKey.has(unit.identity)) {
        byKey.get(unit.identity).count += 1;
      } else {
        const group = { unit, count: 1 };
        byKey.set(unit.identity, group);
        groups.push(group);
      }
    });

    return `
      <ul class="duk-gift-contents" role="list">
        ${groups.map(({ unit, count }) => {
          const label = `${count > 1 ? `${count}× ` : ''}${this.unitLabel(unit)}`;

          return `
            <li class="duk-gift-contents__item" title="${escapeHtml(label)}">
              ${unit.image
                ? `<img class="duk-gift-contents__image" src="${escapeHtml(unit.image)}${unit.image.includes('?') ? '&' : '?'}width=120" alt="${escapeHtml(label)}" width="40" height="47">`
                : `<span class="duk-gift-contents__name">${escapeHtml(label)}</span>`}
              ${unit.image && count > 1 ? `<span class="duk-gift-contents__count" aria-hidden="true">${count}×</span>` : ''}
            </li>
          `;
        }).join('')}
      </ul>
    `;
  }

  renderPapers(number) {
    const papers = papersOf(this.config);

    if (papers.length <= 1) return '';

    const selected = validPaper(this.config, (this.state.messages[number] || {}).paper);
    const giftLabel = this.t('gift', { n: number });

    // Paper photos as tiles, two per row; the chosen one gets a dark frame and a check mark
    return `
      <div class="duk-gift__section">
        <p class="duk-gift__section-label">${escapeHtml(this.t('paper'))}</p>
        <div class="duk-gift__papers" role="group" aria-label="${escapeHtml(this.t('paperGroup', { gift: giftLabel }))}">
          ${papers.map((paper) => `
            <button type="button" class="duk-gift__paper" data-action="paper" data-package="${number}" data-paper="${paper.id}" data-focus-key="paper-${number}-${paper.id}" aria-pressed="${Number(paper.id) === selected}">
              <span class="duk-gift__paper-frame">
                ${paper.image
                  ? `<img class="duk-gift__paper-image" src="${escapeHtml(paper.image)}" alt="" width="400" height="400">`
                  : '<span class="duk-gift__paper-image" aria-hidden="true"></span>'}
                <span class="duk-gift__paper-check" aria-hidden="true">${CHECK_SVG}</span>
              </span>
              <span class="duk-gift__paper-title">${escapeHtml(paper.maker ? `${paper.title} · ${paper.maker}` : paper.title)}</span>
            </button>
          `).join('')}
        </div>
      </div>
    `;
  }

  totalPrice() {
    return this.packages()
      .filter((entry) => entry.units.length > 0)
      .reduce((sum, entry) => sum + priceOf(this.config, (this.state.messages[entry.number] || {}).paper), 0);
  }

  renderSummary() {
    const count = this.packageCount();

    if (this.state.mode === 'single' && count === 0) return escapeHtml(this.t('pickNone'));
    if (this.state.mode === 'split' && count === 0) return escapeHtml(this.t('splitNone'));
    if (this.state.mode === 'none' || count === 0) return escapeHtml(this.t('none'));

    const label = count === 1 ? this.t('giftOne') : this.t('giftMany', { n: count });
    return `${escapeHtml(label)} · ${formatMoney(this.totalPrice())}`;
  }

  render() {
    if (!this.state) return;
    if (glideStop) glideStop(); // the scroll area is rebuilt below

    const scroller = this.querySelector('.duk-gift__scroll');
    const scrollTop = scroller ? scroller.scrollTop : 0;
    const focusKey = this.contains(document.activeElement) ? document.activeElement.getAttribute('data-focus-key') : null;

    if (this.state.units.length === 0) {
      this.innerHTML = `<p class="duk-gift__intro">${escapeHtml(this.t('noItems'))}</p>`;
      return;
    }

    // Mehrere Papiere oder Kartentext: je Geschenk ein Abschnitt (renderPackages), sonst nur die Namen und am Ende
    // „So packen wir ein“ (renderOverview; beim Aufteilen sitzt es in renderSplit)
    const extras = papersOf(this.config).length > 1 || messagesOn(this.config);
    const namesFirst = this.state.mode === 'split' && !extras;

    this.innerHTML = `
      <div class="duk-gift${this.state.units.length > 1 ? ' duk-gift--modes' : ''}" data-context="${escapeHtml(this.getAttribute('context') || '')}">
        <div class="duk-gift__scroll">
          ${this.renderModes()}
          ${this.state.mode === 'together' && extras ? this.renderTogether() : ''}
          ${this.state.mode === 'single' ? this.renderPick() : ''}
          ${this.state.mode === 'split' ? this.renderSplit(namesFirst ? this.renderNames() : '') : ''}
          ${extras ? this.renderPackages() : (namesFirst ? '' : this.renderNames())}
          ${!extras && this.state.mode !== 'split' ? this.renderOverview() : ''}
          ${this.state.initialMode && this.state.initialMode !== 'none' ? `<button type="button" class="duk-gift__link duk-gift__unwrap" data-action="unwrap" data-focus-key="unwrap">${escapeHtml(this.t('none'))}</button>` : ''}
        </div>

        <div class="duk-gift__footer">
          <p class="duk-gift__summary">${this.renderSummary()}</p>
          <button type="button" class="button" data-action="apply" data-focus-key="apply" ${this.blocked() ? 'disabled' : ''}>
            <button-content class="button__content">${escapeHtml(this.t('apply'))}</button-content>
          </button>
        </div>

        <p class="duk-gift__error" role="alert" hidden></p>
      </div>
    `;

    const newScroller = this.querySelector('.duk-gift__scroll');
    if (newScroller) newScroller.scrollTop = scrollTop;

    if (focusKey) {
      this.querySelector(`[data-focus-key="${CSS.escape(focusKey)}"]`)?.focus({ preventScroll: true });
    }
  }

  // Aufteilen, aber noch nichts zugeordnet: nichts übernehmen (auch nicht beim Zurück), sonst verschwände eine bestehende
  // Verpackung unbemerkt. Entfernen geht bewusst über „Ohne Verpackung“.
  blocked() {
    return Boolean(this.state) && this.state.mode === 'split' && this.packageCount() === 0;
  }

  updateSummary() {
    const summary = this.querySelector('.duk-gift__summary');
    if (summary) summary.innerHTML = this.renderSummary();
  }

  showError(message) {
    const error = this.querySelector('.duk-gift__error');

    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
  }

  /* -- Events ---------------------------------------------------------------------------------- */

  onChange = (event) => {
    const input = event.target;

    if (input.type !== 'radio' || !input.name.endsWith('-mode')) return;

    this.dirty = true;
    const from = this.state.mode;
    const byUnit = this.messagesByUnit();
    this.state.mode = input.value;

    if (this.state.mode === 'single' && this.state.picked.size === 0) {
      this.state.picked = new Set(this.state.units.map((unit) => unit.uid));
    }

    // Aufteilen beginnt leer: alles auf „Ohne“, die Kacheln füllen sich beim Zuordnen (Matthias, 06.10.: „verwirrend,
    // wenn schon alle in 1“). Eine echte Aufteilung (zwei oder mehr Geschenke) bleibt stehen.
    if (this.state.mode === 'split') {
      const { units, assign } = this.state;
      const used = new Set(units.map((unit) => assign[unit.uid]).filter((value) => value > 0));

      if (used.size < 2) {
        units.forEach((unit) => { assign[unit.uid] = 0; });
      }

      const highest = Math.max(0, ...units.map((unit) => assign[unit.uid]));
      this.state.count = Math.min(Math.max(this.state.count, highest, 2), units.length);
    }

    this.switchMessages(from, byUnit);

    this.render();
    this.revealChoice();
  };

  // Namen bleiben beim Produkt (1.2.4, Fehlerliste Nr. 10). Jede Art zählt die Geschenke anders (Zusammen: 1, Einzeln:
  // Platz in der Liste, Aufteilen: Geschenknummer), darum hat jede Art ihre eigenen Namen und behält sie beim Hin und Her.
  // Beim ersten Besuch einer Art kommen die Namen aus der vorigen, Produkt für Produkt.
  messagesByUnit() {
    const { mode, units, assign, messages, picked } = this.state;
    const byUnit = new Map();

    units.forEach((unit, index) => {
      let key = 0;
      if (mode === 'together') key = 1;
      if (mode === 'single' && picked.has(unit.uid)) key = index + 1;
      if (mode === 'split') key = assign[unit.uid] || 0;

      const message = key > 0 ? messages[key] : null;
      if (message && (message.to || message.card)) byUnit.set(unit.uid, message);
    });

    return byUnit;
  }

  switchMessages(from, byUnit) {
    const { state } = this;
    const to = state.mode;
    if (from === to) return;

    this.messageStore = this.messageStore || {};
    this.messageStore[from] = state.messages;

    // Schon besucht und mit Namen: so lassen. Ohne Namen (z. B. nur kurz angesehen): neu aus der vorigen Art übernehmen
    const kept = this.messageStore[to];
    if (kept && Object.values(kept).some((message) => message && (message.to || message.card))) {
      state.messages = kept;
      return;
    }

    const messages = {};
    const copy = (message) => ({ ...message });
    const first = (units) => units.map((unit) => byUnit.get(unit.uid)).find(Boolean);

    if (to === 'together') {
      // Nur wenn alle Namen gleich sind; „Anna“ und „Ben“ in einem Geschenk wäre falsch
      const names = [...new Set([...byUnit.values()].map((message) => (message.to || '').trim()).filter(Boolean))];
      if (names.length === 1) messages[1] = copy(first(state.units));
    } else if (to === 'single') {
      state.units.forEach((unit, index) => {
        if (byUnit.has(unit.uid)) messages[index + 1] = copy(byUnit.get(unit.uid));
      });
    } else if (to === 'split') {
      const assigned = state.units.some((unit) => state.assign[unit.uid] > 0);

      if (assigned) {
        for (let number = 1; number <= state.count; number++) {
          const message = first(state.units.filter((unit) => state.assign[unit.uid] === number));
          if (message) messages[number] = copy(message);
        }
      } else {
        // Aufteilen beginnt leer: die schon getippten Namen stehen der Reihe nach bei „Für wen?“
        const seen = new Set();
        const distinct = state.units.map((unit) => byUnit.get(unit.uid)).filter((message) => {
          const name = message && (message.to || '').trim();
          if (!name || seen.has(name)) return false;
          seen.add(name);
          return true;
        });
        distinct.slice(0, state.units.length).forEach((message, index) => { messages[index + 1] = copy(message); });
        state.count = Math.min(Math.max(state.count, distinct.length), state.units.length);
      }
    }

    state.messages = messages;
  }

  // The part below the options where something is left to do: tick items, split into gifts, or (only with a
  // paper choice or card) the one gift. Matthias, 04.10.: „automatisch nach unten gescrollt werden, es rückt sonst
  // nicht ins Sichtfeld“
  revealChoice() {
    const { mode } = this.state;
    let selector = '';

    if (mode === 'single') selector = '.duk-gift__pick';
    if (mode === 'split') selector = '.duk-gift__split';
    if (mode === 'together' && papersOf(this.config).length > 1) selector = '.duk-gift__packages';

    const target = selector ? this.querySelector(selector) : null;
    if (!target) return;

    const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const nextFrame = window.requestAnimationFrame ? window.requestAnimationFrame.bind(window) : (callback) => window.setTimeout(callback, 16);

    nextFrame(() => {
      const scroller = this.querySelector('.duk-gift__scroll');
      const rect = target.getBoundingClientRect();

      // Drawer: the panel scrolls on its own
      if (scroller && scroller.scrollHeight > scroller.clientHeight + 1) {
        const box = scroller.getBoundingClientRect();
        if (rect.top >= box.top && rect.bottom <= box.bottom) return;

        glide(scroller, scroller.scrollTop + rect.top - box.top - 12, reduced);
        return;
      }

      // Cart page: the page scrolls, below the sticky header
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height')) || 0;
      if (rect.top >= header && rect.bottom <= window.innerHeight) return;

      glide(window, window.scrollY + rect.top - header - 16, reduced);
    });
  }

  onInput = (event) => {
    const field = event.target.getAttribute('data-field');

    if (!field) return;

    const number = Number(event.target.getAttribute('data-package'));
    this.dirty = true;
    this.state.messages[number] = { ...(this.state.messages[number] || {}), [field]: event.target.value };
    if (field === 'to') this.paintRecipient(number);
  };

  onToggle = (event) => {
    if (!event.target.matches || !event.target.matches('details[data-package]')) return;

    const number = Number(event.target.getAttribute('data-package'));

    if (event.target.open) {
      this.openDetails.add(number);
    } else {
      this.openDetails.delete(number);
    }
  };

  onClick = (event) => {
    const button = event.target.closest('[data-action]');

    if (!button || !this.contains(button)) return;

    const { state } = this;
    const action = button.getAttribute('data-action');
    if (action !== 'apply' && action !== 'unwrap') this.dirty = true;

    switch (action) {
      case 'decrease':
        if (state.count > 1) {
          state.count -= 1;
          state.units.forEach((unit) => {
            if (state.assign[unit.uid] > state.count) state.assign[unit.uid] = state.count;
          });
          this.render();
        }
        break;

      case 'increase':
        if (state.count < state.units.length) {
          state.count += 1;
          this.render();
        }
        break;

      case 'assign':
        state.assign[button.getAttribute('data-uid')] = Number(button.getAttribute('data-package'));
        this.render();
        break;

      // Geschenk entfernen (ab drei Geschenken, zwei bleiben immer): seine Produkte gehen auf „Ohne“, die Nummern dahinter
      // rücken nach, die Namen wandern mit
      case 'remove-gift': {
        const removed = Number(button.getAttribute('data-package'));
        if (state.count <= 2) break;

        state.units.forEach((unit) => {
          if (state.assign[unit.uid] === removed) state.assign[unit.uid] = 0;
          else if (state.assign[unit.uid] > removed) state.assign[unit.uid] -= 1;
        });

        const messages = {};
        Object.entries(state.messages).forEach(([key, value]) => {
          const number = Number(key);
          if (number < removed) messages[number] = value;
          if (number > removed) messages[number - 1] = value;
        });

        state.messages = messages;
        state.count -= 1;
        this.render();
        break;
      }

      // „Ohne Verpackung“: alle Verpackungen aus dem Warenkorb nehmen
      case 'unwrap':
        state.mode = 'none';
        this.apply(this.querySelector('[data-action="apply"]') || button);
        break;

      case 'pick': {
        const uid = button.getAttribute('data-uid');
        if (state.picked.has(uid)) {
          state.picked.delete(uid);
        } else {
          state.picked.add(uid);
        }
        this.render();
        break;
      }

      case 'pick-all': {
        const all = state.units.every((unit) => state.picked.has(unit.uid));
        state.picked = new Set(all ? [] : state.units.map((unit) => unit.uid));
        this.render();
        break;
      }

      case 'paper': {
        const number = Number(button.getAttribute('data-package'));
        state.messages[number] = { ...(state.messages[number] || {}), paper: Number(button.getAttribute('data-paper')) };
        this.render();
        break;
      }

      case 'apply':
        this.apply(button);
        break;
    }
  };

  async apply(button) {
    const error = this.querySelector('.duk-gift__error');
    if (error) error.hidden = true;

    if (this.applying || this.blocked()) return;
    this.applying = true;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      await serialized(() => this.applyNow());
    } catch (exception) {
      console.warn('[duk-gift] Übernehmen fehlgeschlagen', exception);

      // Show the cart as it really is now, then the message (render() would otherwise clear it). The drawer behind may
      // show lines (and keys) that no longer exist, so the theme draws it again (08.10.: „no valid id or line parameter“)
      try { await this.load(await getCart()); } catch (loadError) { /* keep the current view */ }
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      this.showError(this.t('error'));
    } finally {
      this.applying = false;
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  // Inside the write queue: the cart is read only once every earlier write has finished
  async applyNow() {
    {
      const cart = await getCart();

      if (cartSignature(cart, this.config) !== this.state.signature || cartLayout(cart, this.config) !== this.state.layout) {
        // The cart changed since the panel was opened: rebuild, keep the chosen mode, let the customer check
        const mode = this.state.mode;
        this.state = stateFromCart(cart, this.config);
        this.state.initialMode = this.state.mode;
        this.state.mode = this.state.units.length === 1 && mode !== 'none' ? 'together' : mode;
        this.render();
        this.showError(this.t('changed'));
        return;
      }

      const plan = buildPlan(cart, this.config, this.state.units, this.finalAssignment());
      const result = await executePlan(plan);
      storeMode(this.state.mode);
      this.dirty = false;

      if (this.drawerPopover && this.drawerPopover.hasAttribute('open')) this.drawerPopover.hide();

      if (result) {
        publishCart(result);
      }
    }
  }
}

if (!window.customElements.get('duk-gift-wrap')) {
  window.customElements.define('duk-gift-wrap', DukGiftWrap);
}
